import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Report {
  id: string;
  company_id: string;
  report_type: 'product_discovery' | 'lead_generation';
  week_start: string;
  status: string;
  dripify_data: any[] | null;
}

interface Funnel {
  sends: number;
  accepts: number;
  accept_rate: number | null;
  responses: number;
  response_rate: number | null;
}

interface ReportContent {
  id: string;
  // Computed (read-only)
  sample_note: string | null;
  date_range_start: string | null;
  date_range_end: string | null;
  // Text fields (editable)
  verdict_status: string | null;
  verdict: string | null;
  hypothesis_short: string | null;
  sequence_note: string | null;
  recommendation: string | null;
  // PD extended fields
  icp_brief: { target_persona: string; success_criteria: string; failure_mode: string } | null;
  connecting_thread: { question_answered: string; answer_summary: string; next_question: string; next_campaign: string } | null;
  // LG extended fields
  performance_tier: string | null;
  campaign_setup: { target_audience: string; campaign_type: string; objective: string; success_threshold: string } | null;
  split_test: {
    variants: Array<{ campaign_name: string; sends: number; accepts: number; accept_rate_pct: number; responses: number; response_rate_pct: number; positive_count: number; best_message: number | null }>;
    winner: string; winning_dimension: string; insight: string;
  } | null;
  title_breakdown: Array<{ title: string; count: number; bucket: string; example_outcome: string }> | null;
  cost_per_outcome: { sends_per_accept: number | null; sends_per_response: number | null; sends_per_positive: number | null; note: string } | null;
  // List fields (editable)
  quotes: Quote[] | null;
  objections: Objection[] | null;
  next_steps: NextStep[] | null;
  hot_leads: HotLead[] | null;
  win_loss_summary: WinLoss | null;
  message_performance: MsgPerf | null;
  csr_edits: CsrEdit[];
  ai_draft: boolean;
}

interface Quote    { text: string; lead_name: string | null; lead_linkedin_url: string | null; bucket: string; why_notable: string }
interface Objection { text: string; count: number; implication?: string; reframe?: string }
interface NextStep  { action: string; priority: 'urgent' | 'high' | 'medium' }
interface HotLead   { lead_name: string | null; lead_linkedin_url: string | null; intent_label: string; key_quote: string; suggested_action: string }
interface WinLoss   { positive: number; neutral: number; negative: number; top_positive_label: string; top_negative_label: string }
interface MsgPerf   { breakdown: Record<string, number>; best_message: number | null; insight: string }
interface CsrEdit   { field: string; original: any; value: any; edited_at: string }

interface Group {
  id: string;
  group_key: string;
  group_name: string;
  funnel: Funnel | null;
  classified_breakdown: Record<string, number> | null;
  disposition_breakdown: Record<string, number> | null;
  content: ReportContent | null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FunnelBar({ funnel }: { funnel: Funnel }) {
  const stats = [
    { label: 'Sends', value: funnel.sends, color: 'bg-gray-300' },
    { label: 'Accepts', value: funnel.accepts, sub: funnel.accept_rate != null ? `${funnel.accept_rate}%` : null, color: 'bg-blue-400' },
    { label: 'Responses', value: funnel.responses, sub: funnel.response_rate != null ? `${funnel.response_rate}% of accepts` : null, color: 'bg-purple-400' },
  ];
  return (
    <div className="flex gap-4 mb-5">
      {stats.map((s, i) => (
        <React.Fragment key={s.label}>
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${s.color} text-white font-bold text-lg`}>
              {s.value}
            </div>
            <div className="text-xs text-gray-500 mt-1 font-medium">{s.label}</div>
            {s.sub && <div className="text-xs text-gray-400">{s.sub}</div>}
          </div>
          {i < stats.length - 1 && (
            <div className="self-center text-gray-300 text-lg">→</div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function EditableText({
  label, value, onSave, placeholder,
}: { label: string; value: string | null; onSave: (v: string) => void; placeholder?: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  const handleSave = () => {
    onSave(draft);
    setEditing(false);
  };

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
        {!editing && (
          <button onClick={() => { setDraft(value ?? ''); setEditing(true); }} className="text-gray-400 hover:text-purple-600 text-xs">
            ✏️
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={3}
            className="w-full border border-purple-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            autoFocus
          />
          <div className="flex gap-2 mt-1">
            <button onClick={handleSave} className="px-3 py-1 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700">Save</button>
            <button onClick={() => setEditing(false)} className="px-3 py-1 text-gray-500 text-xs hover:text-gray-700">Cancel</button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-800 leading-relaxed">
          {value || <span className="text-gray-400 italic">{placeholder ?? 'No content generated'}</span>}
        </p>
      )}
    </div>
  );
}

function QuoteList({ quotes, onUpdate }: { quotes: Quote[]; onUpdate: (q: Quote[]) => void }) {
  if (!quotes?.length) return <p className="text-sm text-gray-400 italic mb-4">No quotes captured.</p>;
  const BUCKET_COLOR: Record<string, string> = { positive: 'border-green-300 bg-green-50', neutral: 'border-gray-300 bg-gray-50', negative: 'border-red-300 bg-red-50' };
  return (
    <div className="space-y-2 mb-4">
      {quotes.map((q, i) => (
        <div key={i} className={`border rounded-lg p-3 text-sm ${BUCKET_COLOR[q.bucket] ?? 'border-gray-200 bg-gray-50'}`}>
          <div className="flex justify-between items-start gap-2">
            <div>
              <p className="italic text-gray-700">"{q.text}"</p>
              {q.lead_name && <p className="text-xs text-gray-500 mt-1">— {q.lead_name}</p>}
              {q.why_notable && <p className="text-xs text-gray-500 mt-0.5">{q.why_notable}</p>}
            </div>
            <button onClick={() => onUpdate(quotes.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 text-xs shrink-0">✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ObjectionList({ objections, reportType, onUpdate }: { objections: Objection[]; reportType: string; onUpdate: (o: Objection[]) => void }) {
  if (!objections?.length) return <p className="text-sm text-gray-400 italic mb-4">No objections recorded.</p>;
  return (
    <div className="space-y-2 mb-4">
      {objections.map((o, i) => (
        <div key={i} className="border border-gray-200 rounded-lg p-3 text-sm bg-white">
          <div className="flex justify-between gap-2">
            <div>
              <p className="text-gray-700 font-medium">"{o.text}" <span className="text-gray-400 font-normal">({o.count}×)</span></p>
              {reportType === 'product_discovery' && o.implication && (
                <p className="text-xs text-gray-500 mt-1">→ {o.implication}</p>
              )}
              {reportType === 'lead_generation' && o.reframe && (
                <p className="text-xs text-gray-500 mt-1">Reframe: {o.reframe}</p>
              )}
            </div>
            <button onClick={() => onUpdate(objections.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 text-xs shrink-0">✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function NextStepList({ steps, onUpdate }: { steps: NextStep[]; onUpdate: (s: NextStep[]) => void }) {
  if (!steps?.length) return <p className="text-sm text-gray-400 italic mb-4">No next steps generated.</p>;
  const PRIORITY_BADGE: Record<string, string> = {
    urgent: 'bg-red-100 text-red-700',
    high:   'bg-amber-100 text-amber-700',
    medium: 'bg-blue-100 text-blue-700',
  };
  return (
    <div className="space-y-1.5 mb-4">
      {steps.map((s, i) => (
        <div key={i} className="flex items-start gap-2 text-sm">
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium mt-0.5 shrink-0 ${PRIORITY_BADGE[s.priority] ?? 'bg-gray-100 text-gray-600'}`}>
            {s.priority}
          </span>
          <span className="text-gray-800 flex-1">{s.action}</span>
          <button onClick={() => onUpdate(steps.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 text-xs shrink-0">✕</button>
        </div>
      ))}
    </div>
  );
}

function HotLeadList({ leads }: { leads: HotLead[] }) {
  if (!leads?.length) return <p className="text-sm text-gray-400 italic mb-4">No hot leads this week.</p>;
  return (
    <div className="space-y-3 mb-4">
      {leads.map((l, i) => (
        <div key={i} className="border border-green-200 bg-green-50 rounded-lg p-3 text-sm">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold text-gray-800">
                {l.lead_linkedin_url ? (
                  <a href={l.lead_linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-700">
                    {l.lead_name ?? 'Unknown'}
                  </a>
                ) : (l.lead_name ?? 'Unknown')}
                <span className="ml-2 text-xs font-normal text-green-700 bg-green-100 px-1.5 py-0.5 rounded">{l.intent_label}</span>
              </p>
              {l.key_quote && <p className="italic text-gray-600 mt-1">"{l.key_quote}"</p>}
              <p className="text-xs text-purple-700 mt-1 font-medium">→ {l.suggested_action}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MsgPerfDisplay({ perf }: { perf: MsgPerf }) {
  const total = Object.values(perf.breakdown).reduce((a, b) => a + b, 0);
  return (
    <div className="mb-4">
      <div className="flex gap-3 mb-2">
        {Object.entries(perf.breakdown).sort((a, b) => Number(a[0]) - Number(b[0])).map(([msg, count]) => (
          <div key={msg} className="text-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
              perf.best_message === Number(msg) ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}>
              {count}
            </div>
            <div className="text-xs text-gray-500 mt-1">Msg {msg}</div>
          </div>
        ))}
      </div>
      {perf.insight && <p className="text-sm text-gray-600 italic">{perf.insight}</p>}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const CSRReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<Report | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ groupId: string; text: string } | null>(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [showRerunConfirm, setShowRerunConfirm] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);

  const handleDownloadPDF = async () => {
    if (!report) return;
    setDownloadingPDF(true);
    try {
      const [{ pdf }, { ReportPDF }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../../components/reports/ReportPDF'),
      ]);
      const pdfReport = {
        id: report.id,
        report_type: report.report_type,
        week_start: report.week_start,
        published_at: null as string | null,
      };
      const blob = await pdf(
        <ReportPDF report={pdfReport} groups={groups as any} companyName={companyName} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${report.week_start}-${report.report_type}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleRerun = async () => {
    if (!report) return;
    setShowRerunConfirm(false);
    setRerunError(null);
    setRerunning(true);
    try {
      const { error } = await supabase.functions.invoke('synthesize-report', {
        body: { company_id: report.company_id, week_start: report.week_start },
      });
      if (error) throw error;
      await load();
    } catch (err: any) {
      setRerunError(err?.message ?? 'Re-run failed. Try again.');
    } finally {
      setRerunning(false);
    }
  };

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);

    const { data: rpt } = await supabase
      .from('customer_reports')
      .select('id, company_id, report_type, week_start, status, dripify_data')
      .eq('id', id)
      .single();

    if (!rpt) { setLoading(false); return; }
    setReport(rpt as Report);

    const { data: comp } = await supabase.from('companies').select('name').eq('id', rpt.company_id).single();
    setCompanyName(comp?.name ?? '');

    const { data: groupData } = await supabase
      .from('report_campaign_groups')
      .select('id, group_key, group_name, funnel, classified_breakdown, disposition_breakdown')
      .eq('report_id', id)
      .order('group_key');

    const groupIds = (groupData ?? []).map((g: any) => g.id);
    const { data: contentData } = await supabase
      .from('report_content')
      .select('*')
      .in('group_id', groupIds);

    const contentByGroup: Record<string, ReportContent> = {};
    (contentData ?? []).forEach((c: any) => { contentByGroup[c.group_id] = c; });

    setGroups(
      (groupData ?? []).map((g: any) => ({
        ...g,
        content: contentByGroup[g.id] ?? null,
      }))
    );
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveField = async (group: Group, field: string, value: any) => {
    if (!group.content) return;
    const content = group.content;

    // Append to csr_edits audit trail
    const newEdit: CsrEdit = {
      field,
      original: (content as any)[field],
      value,
      edited_at: new Date().toISOString(),
    };
    const edits = [...(content.csr_edits ?? []), newEdit];

    const { error } = await supabase
      .from('report_content')
      .update({ [field]: value, csr_edits: edits, ai_draft: false })
      .eq('id', content.id);

    if (error) {
      setSaveMsg({ groupId: group.id, text: `Save failed: ${error.message}` });
    } else {
      setSaveMsg({ groupId: group.id, text: 'Saved.' });
      setTimeout(() => setSaveMsg(null), 2500);
      // Update local state
      setGroups(prev => prev.map(g =>
        g.id === group.id
          ? { ...g, content: { ...g.content!, [field]: value, csr_edits: edits, ai_draft: false } }
          : g
      ));
    }
  };

  const handlePublish = async () => {
    if (!report) return;
    setPublishing(true);
    await supabase
      .from('customer_reports')
      .update({ status: 'published', published_at: new Date().toISOString() })
      .eq('id', report.id);
    setReport(prev => prev ? { ...prev, status: 'published' } : prev);
    setPublishing(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-sm text-gray-500">Loading report...</div>;
  }
  if (!report) {
    return <div className="p-8 text-center text-sm text-red-500">Report not found.</div>;
  }

  const weekStart = new Date(report.week_start + 'T00:00:00');
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const isProductDiscovery = report.report_type === 'product_discovery';
  const isPublished = report.status === 'published';
  const hasAnyEdits = groups.some(g => (g.content?.csr_edits?.length ?? 0) > 0);
  const allAiDraft = groups.length > 0 && groups.every(g => !g.content || g.content.ai_draft === true);
  const canRerun = !isPublished && allAiDraft && !hasAnyEdits;
  const rerunBlocked = report.status === 'running';

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button onClick={() => navigate('/csr-reports')} className="text-sm text-gray-400 hover:text-gray-600 mb-2 flex items-center gap-1">
            ← Back to Reports
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{companyName}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isProductDiscovery ? '🔬 Product Discovery' : '🎯 Lead Generation'} · {fmt(weekStart)} – {fmt(weekEnd)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canRerun && (
            <button
              onClick={() => { setShowRerunConfirm(true); setRerunError(null); }}
              disabled={rerunning || rerunBlocked}
              className="flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors"
            >
              {rerunning ? (
                <>
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Regenerating…
                </>
              ) : rerunBlocked ? (
                '⏳ Report running…'
              ) : (
                '↺ Re-run AI Draft'
              )}
            </button>
          )}
          <button
            onClick={handleDownloadPDF}
            disabled={downloadingPDF}
            className="flex items-center gap-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors"
          >
            {downloadingPDF ? (
              <>
                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Generating…
              </>
            ) : (
              <>
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Preview PDF
              </>
            )}
          </button>
          {isPublished ? (
            <span className="px-3 py-1.5 bg-green-100 text-green-700 text-sm font-medium rounded-lg">✓ Published</span>
          ) : (
            <button
              onClick={handlePublish}
              disabled={publishing}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {publishing ? 'Publishing...' : 'Publish Report'}
            </button>
          )}
        </div>
      </div>

      {/* Re-run confirmation panel */}
      {showRerunConfirm && (
        <div className="mb-5 border border-amber-300 bg-amber-50 rounded-xl px-5 py-4">
          <p className="text-sm font-semibold text-amber-800 mb-1">Regenerate AI draft?</p>
          <p className="text-xs text-amber-700 mb-3">
            This will re-run the full synthesis for this report. The current AI draft will be replaced.
            This cannot be undone.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleRerun}
              className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 transition-colors"
            >
              Yes, regenerate
            </button>
            <button
              onClick={() => setShowRerunConfirm(false)}
              className="px-3 py-1.5 text-amber-700 text-xs hover:text-amber-900"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Re-run error */}
      {rerunError && (
        <div className="mb-5 border border-red-200 bg-red-50 rounded-xl px-5 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{rerunError}</span>
          <button onClick={() => setRerunError(null)} className="text-red-400 hover:text-red-600 text-xs ml-3">✕</button>
        </div>
      )}

      {groups.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400">
          No campaign groups found in this report.
        </div>
      )}

      {/* Campaign groups */}
      {groups.map(group => {
        const c = group.content;
        const groupSaveMsg = saveMsg?.groupId === group.id ? saveMsg.text : null;

        return (
          <div key={group.id} className="border border-gray-200 rounded-2xl bg-white mb-6 overflow-hidden">
            {/* Group header */}
            <div className="bg-gray-50 border-b border-gray-200 px-5 py-3 flex items-center justify-between">
              <div>
                <span className="font-semibold text-gray-800">{group.group_name}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                {group.classified_breakdown && (
                  <>
                    <span className="text-green-600">+{group.classified_breakdown.positive ?? 0}</span>
                    <span className="text-gray-400">{group.classified_breakdown.neutral ?? 0} neutral</span>
                    <span className="text-red-500">−{group.classified_breakdown.negative ?? 0}</span>
                  </>
                )}
                {groupSaveMsg && <span className="text-purple-600">{groupSaveMsg}</span>}
              </div>
            </div>

            <div className="px-5 py-4">
              {/* Funnel */}
              {group.funnel && <FunnelBar funnel={group.funnel} />}

              {/* Sample note */}
              {c?.sample_note && (
                <p className="text-xs text-gray-500 mb-4 border-l-2 border-gray-200 pl-2">{c.sample_note}</p>
              )}

              {c ? (
                <>
                  {/* Verdict status badge — PD only */}
                  {isProductDiscovery && c.verdict_status && (() => {
                    const STATUS_STYLE: Record<string, string> = {
                      'CONFIRMED':    'bg-green-100 text-green-800 border-green-300',
                      'DENIED':       'bg-red-100 text-red-800 border-red-300',
                      'EARLY SIGNAL': 'bg-blue-100 text-blue-800 border-blue-300',
                      'MIXED':        'bg-amber-100 text-amber-800 border-amber-300',
                      'INCONCLUSIVE': 'bg-gray-100 text-gray-600 border-gray-300',
                    };
                    return (
                      <div className="mb-3">
                        <span className={`inline-block text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border ${STATUS_STYLE[c.verdict_status] ?? 'bg-gray-100 text-gray-600 border-gray-300'}`}>
                          {c.verdict_status}
                        </span>
                      </div>
                    );
                  })()}

                  {/* Performance tier badge — LG only */}
                  {!isProductDiscovery && c.performance_tier && (() => {
                    const TIER_STYLE: Record<string, string> = {
                      'STRONG':   'bg-green-100 text-green-800 border-green-300',
                      'MODERATE': 'bg-blue-100 text-blue-800 border-blue-300',
                      'EARLY':    'bg-amber-100 text-amber-800 border-amber-300',
                      'WATCH':    'bg-red-100 text-red-800 border-red-300',
                    };
                    return (
                      <div className="mb-3">
                        <span className={`inline-block text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border ${TIER_STYLE[c.performance_tier] ?? 'bg-gray-100 text-gray-600 border-gray-300'}`}>
                          {c.performance_tier}
                        </span>
                      </div>
                    );
                  })()}

                  {/* Verdict */}
                  <EditableText
                    label="Verdict"
                    value={c.verdict}
                    onSave={v => saveField(group, 'verdict', v)}
                  />

                  {/* Product Discovery only */}
                  {isProductDiscovery && (
                    <EditableText
                      label="Hypothesis"
                      value={c.hypothesis_short}
                      onSave={v => saveField(group, 'hypothesis_short', v)}
                    />
                  )}

                  {/* PD: ICP Brief */}
                  {isProductDiscovery && c.icp_brief && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Campaign Brief</p>
                      <div className="border border-gray-200 rounded-lg overflow-hidden text-sm">
                        {[
                          { label: 'Target Persona',   value: c.icp_brief.target_persona },
                          { label: 'Success Criteria', value: c.icp_brief.success_criteria },
                          { label: 'Failure Mode',     value: c.icp_brief.failure_mode },
                        ].map((row, i) => (
                          <div key={i} className={`flex ${i > 0 ? 'border-t border-gray-200' : ''}`}>
                            <div className="w-36 shrink-0 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 border-r border-gray-200">{row.label}</div>
                            <div className="px-3 py-2 text-gray-700 text-xs leading-relaxed">{row.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* LG: Campaign Setup */}
                  {!isProductDiscovery && c.campaign_setup && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Campaign Setup</p>
                      <div className="border border-gray-200 rounded-lg overflow-hidden text-sm">
                        {[
                          { label: 'Target Audience',   value: c.campaign_setup.target_audience },
                          { label: 'Campaign Type',     value: c.campaign_setup.campaign_type },
                          { label: 'Objective',         value: c.campaign_setup.objective },
                          { label: 'Success Threshold', value: c.campaign_setup.success_threshold },
                        ].map((row, i) => (
                          <div key={i} className={`flex ${i > 0 ? 'border-t border-gray-200' : ''}`}>
                            <div className="w-36 shrink-0 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 border-r border-gray-200">{row.label}</div>
                            <div className="px-3 py-2 text-gray-700 text-xs leading-relaxed">{row.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* LG: Split Test Comparison */}
                  {!isProductDiscovery && c.split_test && c.split_test.variants.length > 1 && (() => {
                    const st = c.split_test!;
                    const BUCKET_PILL: Record<string, string> = { positive: 'bg-green-100 text-green-700', neutral: 'bg-gray-100 text-gray-600', negative: 'bg-red-100 text-red-600' };
                    return (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Split Test Comparison</p>
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="grid" style={{ gridTemplateColumns: `repeat(${st.variants.length}, 1fr)` }}>
                            {st.variants.map((v, vi) => {
                              const isWinner = v.campaign_name === st.winner;
                              return (
                                <div key={vi} className={`px-3 py-3 text-xs ${vi > 0 ? 'border-l border-gray-200' : ''} ${isWinner ? 'bg-green-50' : 'bg-white'}`}>
                                  <div className="flex items-center gap-1 mb-2 flex-wrap">
                                    <span className="font-semibold text-gray-700 text-xs">{v.campaign_name}</span>
                                    {isWinner && <span className="text-xs font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">Winner</span>}
                                  </div>
                                  <div className="space-y-1">
                                    {[
                                      ['Sends', v.sends],
                                      ['Accept rate', `${v.accept_rate_pct}%`],
                                      ['Responses', v.responses],
                                      ['Response rate', `${v.response_rate_pct}%`],
                                      ['Positive', v.positive_count],
                                    ].map(([label, val], li) => (
                                      <div key={li} className="flex justify-between">
                                        <span className="text-gray-500">{label}</span>
                                        <span className={`font-medium ${isWinner && (label === 'Response rate' || label === 'Accept rate') ? 'text-green-700' : 'text-gray-800'}`}>{val}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {st.insight && (
                            <div className="bg-gray-50 border-t border-gray-200 px-3 py-2 text-xs text-gray-600 italic">{st.insight}</div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Sequence note */}
                  {c.sequence_note && (
                    <EditableText
                      label="Sequence Note"
                      value={c.sequence_note}
                      onSave={v => saveField(group, 'sequence_note', v)}
                    />
                  )}

                  {/* Lead Gen: Hot Leads */}
                  {!isProductDiscovery && (
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Hot Leads</p>
                      <HotLeadList leads={c.hot_leads ?? []} />
                    </div>
                  )}

                  {/* LG: Who's Responding (title breakdown) */}
                  {!isProductDiscovery && c.title_breakdown && c.title_breakdown.length > 0 && (() => {
                    const BUCKET_PILL: Record<string, string> = { positive: 'bg-green-100 text-green-700', neutral: 'bg-gray-100 text-gray-600', negative: 'bg-red-100 text-red-600' };
                    return (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Who's Responding</p>
                        <div className="space-y-1.5">
                          {c.title_breakdown.map((item, ti) => (
                            <div key={ti} className="flex items-center gap-2 text-xs">
                              <span className={`shrink-0 font-semibold px-2 py-0.5 rounded-full ${BUCKET_PILL[item.bucket] ?? BUCKET_PILL.neutral}`}>{item.bucket}</span>
                              <span className="font-medium text-gray-800 flex-1">{item.title}</span>
                              {item.count > 1 && <span className="text-gray-400">×{item.count}</span>}
                              <span className="text-gray-500 shrink-0">{item.example_outcome}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Lead Gen: Win/Loss + Message Performance */}
                  {!isProductDiscovery && c.win_loss_summary && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Outcome Breakdown</p>
                      <div className="flex gap-4 text-sm">
                        <span className="text-green-600 font-medium">+{c.win_loss_summary.positive} positive</span>
                        <span className="text-gray-500">{c.win_loss_summary.neutral} neutral</span>
                        <span className="text-red-500">−{c.win_loss_summary.negative} negative</span>
                      </div>
                      {c.win_loss_summary.top_positive_label && (
                        <p className="text-xs text-gray-500 mt-1">Top positive: {c.win_loss_summary.top_positive_label}</p>
                      )}
                    </div>
                  )}

                  {!isProductDiscovery && c.message_performance && (
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Message Performance</p>
                      <MsgPerfDisplay perf={c.message_performance} />
                    </div>
                  )}

                  {/* LG: Cost Per Outcome */}
                  {!isProductDiscovery && c.cost_per_outcome && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cost Per Outcome</p>
                      <div className="flex gap-3">
                        {[
                          { label: 'Sends / accept',   value: c.cost_per_outcome.sends_per_accept },
                          { label: 'Sends / response', value: c.cost_per_outcome.sends_per_response },
                          { label: 'Sends / positive', value: c.cost_per_outcome.sends_per_positive },
                        ].filter(s => s.value != null).map((s, si) => (
                          <div key={si} className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-center">
                            <div className="text-lg font-bold text-gray-800">{s.value}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                          </div>
                        ))}
                      </div>
                      {c.cost_per_outcome.note && (
                        <p className="text-xs text-gray-400 italic mt-1.5">{c.cost_per_outcome.note}</p>
                      )}
                    </div>
                  )}

                  {/* Product Discovery: Quotes */}
                  {isProductDiscovery && (
                    <div className="mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Quotes</p>
                        {(c.quotes?.length ?? 0) > 0 && (
                          <button
                            onClick={() => saveField(group, 'quotes', c.quotes)}
                            className="text-xs text-purple-600 hover:text-purple-700"
                          >
                            Save changes
                          </button>
                        )}
                      </div>
                      <QuoteList
                        quotes={c.quotes ?? []}
                        onUpdate={q => {
                          setGroups(prev => prev.map(g =>
                            g.id === group.id ? { ...g, content: { ...g.content!, quotes: q } } : g
                          ));
                        }}
                      />
                      {(c.quotes?.length ?? 0) > 0 && (
                        <button
                          onClick={() => saveField(group, 'quotes', c.quotes)}
                          className="text-xs text-purple-600 hover:text-purple-700 -mt-2 mb-4 block"
                        >
                          Save quote changes
                        </button>
                      )}
                    </div>
                  )}

                  {/* Objections */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Objections</p>
                    </div>
                    <ObjectionList
                      objections={c.objections ?? []}
                      reportType={report.report_type}
                      onUpdate={o => {
                        setGroups(prev => prev.map(g =>
                          g.id === group.id ? { ...g, content: { ...g.content!, objections: o } } : g
                        ));
                        saveField(group, 'objections', o);
                      }}
                    />
                  </div>

                  {/* Recommendation */}
                  <EditableText
                    label="Recommendation"
                    value={c.recommendation}
                    onSave={v => saveField(group, 'recommendation', v)}
                  />

                  {/* Next Steps */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Next Steps</p>
                    <NextStepList
                      steps={c.next_steps ?? []}
                      onUpdate={s => {
                        setGroups(prev => prev.map(g =>
                          g.id === group.id ? { ...g, content: { ...g.content!, next_steps: s } } : g
                        ));
                        saveField(group, 'next_steps', s);
                      }}
                    />
                  </div>

                  {/* PD: Research Thread */}
                  {isProductDiscovery && c.connecting_thread && c.connecting_thread.question_answered && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Research Thread</p>
                      <div className="border border-gray-200 rounded-lg overflow-hidden text-sm">
                        {[
                          { label: 'Question Answered', value: c.connecting_thread.question_answered },
                          { label: 'Answer Summary',    value: c.connecting_thread.answer_summary },
                          { label: 'Next Question',     value: c.connecting_thread.next_question },
                          { label: 'Next Campaign',     value: c.connecting_thread.next_campaign },
                        ].map((row, i) => (
                          <div key={i} className={`flex ${i > 0 ? 'border-t border-gray-200' : ''}`}>
                            <div className="w-36 shrink-0 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 border-r border-gray-200">{row.label}</div>
                            <div className="px-3 py-2 text-gray-700 text-xs leading-relaxed">{row.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {c.ai_draft && (
                    <p className="text-xs text-amber-500 mt-2">AI draft — review and edit before publishing.</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400 italic py-4">Content not yet generated for this group.</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CSRReportDetail;
